import * as React from "react";
import { observer } from "mobx-react";
import { AriaRoles, generateDomPropertiesForAria } from "owa-accessibility";
import { Button, ToolbarDivider } from "@microsoft/shifts-ui-core";
import { TableSparkle24Regular, DismissRegular } from "@fluentui/react-icons";
import AutomationUtils from "sh-application/utility/AutomationUtil";
import { getStrings } from "@microsoft/shifts-experiences-shared";
import { FilterableCommandBarV2, FilterableCommandBarItemV2, DisplayOption, usePanelHeaderStyles} from "../../commandbar";
import { PreviewBadge } from "../../common/PreviewBadge/PreviewBadge";
import { ECSConfigKey, ECSConfigService } from "../../../../sh-services";
import { Title1 } from "@fluentui/react-components";

export interface ScheduleGenerationEditorPageHeaderProps {
    /**
     * Callback invoked when the Save & Close button is clicked
     */
    onSaveAndClose: () => void;

    /**
     * Callback invoked when the Generate Schedule button is clicked
     */
    onGenerate: () => void;

    /**
     * Callback invoked when the Cancel/Close button is clicked
     */
    onClose: () => void;

    /**
     * Whether the generate button should be disabled (e.g., during generation)
     */
    isGenerating: boolean;

    /**
     * Ref to be attached to the Save & Close button for focus management
     */
    saveAndCloseButtonRef?: React.RefObject<HTMLButtonElement>;
}

const ScheduleGenerationEditorPageHeader: React.FC<ScheduleGenerationEditorPageHeaderProps> = ({
    onSaveAndClose,
    onGenerate,
    onClose,
    isGenerating,
    saveAndCloseButtonRef
}) => {
    const panelHeaderStyles = usePanelHeaderStyles();
    const isSmartSchedulePreviewEnabled = ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableSmartSchedulingPreviewTag);

    const _renderHeaderTitle = (): React.ReactNode => {
        return (
            <Title1 as="h1" {...generateDomPropertiesForAria({level: 1, role: 'heading'})} className={panelHeaderStyles.headerTitle}>
                <TableSparkle24Regular />
                {getStrings("smartSchedule", "autoSchedule")}
                { isSmartSchedulePreviewEnabled && <PreviewBadge/> }
            </Title1>
        );
    };

    const _renderSaveAndCloseButton = (): React.ReactNode => {
        const label = getStrings("smartSchedule", "saveAndClose");

        return (
            <Button
                ref={saveAndCloseButtonRef}
                appearance="secondary"
                role={AriaRoles.menuitem}
                onClick={onSaveAndClose}
                title={label}
                className={panelHeaderStyles.actionButton}>
                {label}
            </Button>
        );
    };

    const _renderGenerateButton = (): React.ReactNode => {
        const generateButtonLabel = getStrings("smartSchedule", "generateShiftSchedule");

        return (
            <Button
                appearance="primary"
                role={AriaRoles.menuitem}
                disabled={isGenerating}
                onClick={onGenerate}
                data-automation-id={AutomationUtils.getAutomationId("scheduler", "QAIDSave")}
                title={generateButtonLabel}
                className={panelHeaderStyles.actionButton}>
                {generateButtonLabel}
            </Button>
        );
    };

    const _renderCancelButton = (): React.ReactNode => {
        const cancelButtonLabel = getStrings("common", "cancel");

        return (
            <Button
                appearance="transparent"
                icon={<DismissRegular />}
                role={AriaRoles.menuitem}
                onClick={onClose}
                title={cancelButtonLabel}
                className={panelHeaderStyles.cancelButton}>
            </Button>
        );
    };

    const _getHeaderItems = (): FilterableCommandBarItemV2[] => {
        return [
            FilterableCommandBarItemV2.create({
                key: "header",
                iconOnly: false,
                name: getStrings("smartSchedule", "autoSchedule"),
                role: AriaRoles.heading,
                tabIndex: -1,
                title: getStrings("smartSchedule", "autoSchedule"),
                onRender: _renderHeaderTitle
            }),
            FilterableCommandBarItemV2.create({
                key: "saveBtn",
                name: "save_button",
                iconOnly: false,
                displayOption: DisplayOption.Right,
                role: AriaRoles.button,
                tabIndex: 0,
                onRender: _renderSaveAndCloseButton
            }),
            FilterableCommandBarItemV2.create({
                key: "generateBtn",
                name: "generate_button",
                iconOnly: false,
                displayOption: DisplayOption.Right,
                role: AriaRoles.button,
                tabIndex: 0,
                onRender: _renderGenerateButton
            }),
            FilterableCommandBarItemV2.create({
                key: "divider",
                name: "divider",
                iconOnly: true,
                displayOption: DisplayOption.Right,
                tabIndex: -1,
                onRender: () => (
                    <div className={panelHeaderStyles.dividerContainer}>
                        <ToolbarDivider className={panelHeaderStyles.divider}/>
                    </div>
                )
            }),
            FilterableCommandBarItemV2.create({
                key: "cancelBtn1",
                name: "cancel_button",
                iconOnly: false,
                displayOption: DisplayOption.Right,
                role: AriaRoles.button,
                tabIndex: 0,
                onRender: _renderCancelButton
            })
        ];
    };

    const _renderHeader = () => {
        const headerItems = _getHeaderItems();
        return (
                <FilterableCommandBarV2
                    items={headerItems}
                    ariaLabel={getStrings("smartSchedule", "autoSchedule")}
                    style={panelHeaderStyles}
                    size={"large"}
                />
        );
    };

    return _renderHeader();
};

export default observer(ScheduleGenerationEditorPageHeader);